import React from "react";
import TopBanner from "../components/TopBanner";
import { CTAButton, CTAButtonWrapper } from "../components/HeroSection";
import { ContentWrapper } from "./InsuranceGuidance";
import styled from "styled-components";
import StepsTimeline from "../components/StepsTimeline";
import { ImportantNote, NoteIcon, NoteText } from "./Advisors";

const SectionBlock = styled.div`
  margin: 60px 0;

  h1 {
    font-size: 2rem;
    margin-bottom: 10px;
  }

  h2 {
    font-size: 1.7rem;
    margin-bottom: 16px;
    color: #0f172a;

    span {
      color: #005f73;
    }
  }

  h3 {
    font-weight: 500;
    color: #1e293b;
    margin-bottom: 10px;
  }

  h4 {
    font-size: 1.1rem;
    color: #334155;
    margin-top: 10px;
  }

  p {
    color: #475569;
    line-height: 1.7;
    margin-bottom: 12px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-top: 20px;
`;

const Item = styled.div`
  display: flex;
  gap: 16px;
  padding: 20px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #eaeaea;
  position: relative;
  transition: 0.3s;

  &:hover {
    border-color: #005f73;
    transform: translateY(-4px);
    box-shadow: 0 12px 35px rgba(0, 0, 0, 0.06);
  }
`;

const LeftLine = styled.div`
  width: 4px;
  background: #005f73;
  border-radius: 3px;
`;

const Content = styled.div`
  h5 {
    margin: 0 0 6px;
    color: #0f172a;
  }

  p {
    margin: 0;
    color: #475569;
  }
`;

const Checklist = styled.div`
  display: grid;
  gap: 12px;
  margin-top: 10px;
`;

const CheckItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: "✔";
    width: 18px;
    height: 18px;
    background: #005f73;
    color: #fff;
    border-radius: 50%;
    font-size: 11px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const CTASection = styled.div`
  margin-top: 60px;
  padding: 30px;
  border-radius: 16px;
  background: linear-gradient(to right, #f8fafc, #f1f5f9);
  border: 1px solid #eaeaea;
  text-align: center;

  h2 span {
    color: #005f73;
  }
`;

const WealthiesShield = ({ openModal }) => {
  React.useEffect(() => {
    document.title =
      "Wealthies Shield – Insurance Support & Claim Guidance Program | Wealthies";
  }, []);

  const data = [
    {
      title: "Claim Process Guidance",
      desc: "Understand how insurance claims generally work, what steps to follow, and how to prepare required documentation before approaching the insurer.",
    },
    {
      title: "Policy Renewal Guidance",
      desc: "Get clarity on renewal timelines and understand how to maintain continuous coverage without policy lapses.",
    },
    {
      title: "Documentation Understanding",
      desc: "Learn what documents are typically required for claims and policy servicing to avoid confusion during important situations.",
    },
    {
      title: "Advisor Reconnection Support",
      desc: "Reconnect with licensed insurance professionals for further explanation and support when required.",
    },
  ];

  const steps = [
    {
      title: "Connect Through Wealthies",
      desc: "Engage with licensed insurance professionals through the Wealthies platform.",
    },
    {
      title: "Understand Your Policy",
      desc: "Access guides and support to better understand your coverage.",
    },
    {
      title: "Get Guidance When Needed",
      desc: "Receive structured guidance related to claims, renewals, and documentation.",
    },
  ];

  return (
    <>
      <TopBanner content={"Wealthies Shield"} />

      <ContentWrapper>
        <div className="text-content">
          {/* HERO */}
          <SectionBlock>
            <h1>
              Worried About <span>Insurance Claims</span> or Policy Confusion?
            </h1>
            <h3>Get Guidance. Stay Informed. Stay Protected.</h3>

            <p>
              Understanding insurance doesn’t end at buying a policy. Many
              individuals face uncertainty during claims, renewals, and
              documentation processes.
            </p>

            <p>
              Wealthies Shield is designed to provide structured guidance and
              support so you can better understand your insurance journey at
              every stage.
            </p>

            <h4>Support Before, During, and After Insurance Decisions</h4>

            <p>Available for customers connected through Wealthies</p>
          </SectionBlock>

          {/* WHAT WE HELP WITH */}
          <SectionBlock>
            <h2>
              What <span>Wealthies Shield</span> Helps You With
            </h2>

            <Wrapper>
              {data.map((item, index) => (
                <Item key={index}>
                  <LeftLine />
                  <Content>
                    <h5>{item.title}</h5>
                    <p>{item.desc}</p>
                  </Content>
                </Item>
              ))}
            </Wrapper>
          </SectionBlock>

          {/* EXPLANATION */}
          <SectionBlock>
            <h2>
              Insurance <span>Should Not Feel Confusing</span>
            </h2>

            <p>
              Many people purchase insurance but later struggle to understand
              policy terms, claim procedures, or renewal timelines.
            </p>

            <p>
              Wealthies Shield is designed to simplify these processes and
              provide clarity so individuals can feel more confident while
              navigating insurance-related situations.
            </p>
          </SectionBlock>

          {/* HOW IT WORKS */}
          <SectionBlock>
            <h2>
              How <span>Wealthies Shield</span> Works
            </h2>

            <StepsTimeline steps={steps} />
          </SectionBlock>

          {/* WHY SECTION */}
          <SectionBlock>
            <h2>
              Why <span>Wealthies Shield</span>
            </h2>

            <Checklist>
              <CheckItem>Guidance-focused approach</CheckItem>
              <CheckItem>Helps simplify complex insurance processes</CheckItem>
              <CheckItem>Designed for long-term customer support</CheckItem>
              <CheckItem>Complements your insurance journey</CheckItem>
            </Checklist>
          </SectionBlock>

          {/* CTA */}
          <CTASection>
            <h2>
              Need help <span>understanding</span> your insurance?
            </h2>

            <p>
              Get clarity on claims, renewals, and policy processes with
              Wealthies Shield.
            </p>

            <CTAButtonWrapper>
              <CTAButton onClick={openModal}>
                Activate Wealthies Shield Support
              </CTAButton>
            </CTAButtonWrapper>
          </CTASection>

          {/* NOTE */}
          <ImportantNote>
            <NoteIcon>i</NoteIcon>
            <NoteText>
              Includes members with relevant experience, including
              IRDAI-certified professionals where applicable, who assist in
              explaining general processes related to claims and renewals.
            </NoteText>
          </ImportantNote>
        </div>
      </ContentWrapper>
    </>
  );
};

export default WealthiesShield;
